import {
  FileHandler,
  FileUploaderModalButtons
} from '@library/components/modals/FileUploaderModal';
import { useCallback, useEffect, useState } from 'react';
import type { ImageFileTypes } from '@library/types/polls';
import { Loading } from '@library/components/Loading';
import type { QueryMutationError } from '@bo-types/common';

export interface FileUploaderProps {
  additionalMessageInfo?: string;
  className?: string;
  fileType?: 'csv' | ImageFileTypes;
  isCorrelations?: boolean;
  isUploadError: boolean;
  isUploading: boolean;
  isUploadSuccess: boolean;
  loadingMessage?: string;
  onCancel: () => void;
  onFileUpload: (files: File | null) => void;
  resetUploadAPI: () => void;
  uploadError: QueryMutationError;
}

export const FileUploader = ({
  additionalMessageInfo = '',
  className = '',
  fileType = 'csv',
  isCorrelations = false,
  isUploadError,
  isUploading,
  isUploadSuccess,
  loadingMessage = '',
  onCancel,
  onFileUpload,
  resetUploadAPI,
  uploadError
}: FileUploaderProps) => {
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  useEffect(() => {
    if (isUploadSuccess) {
      setUploadFile(null);
    }
  }, [isUploadSuccess]);

  const handleRemoveFile = useCallback(() => {
    setUploadFile(null);
    resetUploadAPI();
  }, [setUploadFile, resetUploadAPI]);

  const handleFileDrop = useCallback(
    (file: File | null) => {
      setUploadFile(file);
      resetUploadAPI();
    },
    [setUploadFile, resetUploadAPI]
  );

  const handleFileChange = useCallback(
    (file: File | null) => {
      setUploadFile(file);
      resetUploadAPI();
    },
    [setUploadFile, resetUploadAPI]
  );

  const handleFileUpload = useCallback(() => {
    onFileUpload(uploadFile ?? null);
  }, [uploadFile, onFileUpload]);

  const hasErrorMessage = uploadError && 'message' in uploadError;

  const errorMessage = hasErrorMessage
    ? uploadError.message
    : `Some or all rows in ${fileType.toUpperCase()} upload have failed`;

  if (isUploading) {
    return (
      <div className="h-72">
        <Loading
          loadingMessage={
            loadingMessage ||
            `Validating ${fileType.toUpperCase()} and updating ${isCorrelations ? 'correlations' : 'projections'}`
          }
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <FileHandler
        fileType={fileType}
        onFileChange={handleFileChange}
        onFileDrop={handleFileDrop}
        onRemoveFile={handleRemoveFile}
        uploadFile={uploadFile}
      />
      <FileUploaderModalButtons
        disabled={!uploadFile}
        onCancel={onCancel}
        onFileUpload={handleFileUpload}
      />
      {isUploadError && (
        <p className="text-roses">{`Error: ${errorMessage ?? ''}`}</p>
      )}
      {isUploadSuccess && (
        <p className="text-atlien-200">
          {`${fileType.toUpperCase()} File Uploaded Successfully.`}
          {additionalMessageInfo}
        </p>
      )}
    </div>
  );
};
